import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  PrescriptionActionTypes,
  PrescriptionsPageRequested,
  PrescriptionsPageLoaded,
  PrescriptionCreated,
  PrescriptionDeleted,
  PrescriptionUpdated,
  PrescriptionOnServerCreated,
  PrescriptionsActionToggleLoading,
  PrescriptionsPageToggleLoading,
  ThrowPrescriptionError,
  FirePrescriptionSuccess,
  PrescriptionOnServerUpdated,
  PrescriptionsPageCancelled,
  PrescriptionOnServerDeleted,
} from '../_actions/prescription.actions';
import { Router } from '@angular/router';
import { PrescriptionService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class PrescriptionEffects {
  showPageLoadingDistpatcher = new PrescriptionsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new PrescriptionsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new PrescriptionsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PrescriptionsActionToggleLoading({ isLoading: false });

  @Effect()
  loadPrescriptionsPage$ = this.actions$.pipe(
    ofType<PrescriptionsPageRequested>(PrescriptionActionTypes.PrescriptionsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.prescription.findPrescriptions(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            return new PrescriptionsPageCancelled();
          }
          const result = response[0];
          return new PrescriptionsPageLoaded({
            prescriptions: result.data,
            totalCount: result.count,
            page: response[1],
          });
        }),
      );
    }),
  );

  @Effect()
  deletePrescription$ = this.actions$.pipe(
    ofType<PrescriptionOnServerDeleted>(PrescriptionActionTypes.PrescriptionOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.prescription.deletePrescription(payload.id, payload.patientId).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPrescriptionError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePrescriptionSuccess({
                action_type: PrescriptionActionTypes.PrescriptionDeleted,
              }),
            );
            return new PrescriptionDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePrescription$ = this.actions$.pipe(
    ofType<PrescriptionOnServerUpdated>(PrescriptionActionTypes.PrescriptionOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.prescription.updatePrescription(payload.prescription, payload.patientId).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPrescriptionError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePrescriptionSuccess({
                action_type: PrescriptionActionTypes.PrescriptionUpdated,
              }),
            );
            return new PrescriptionUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createPrescription$ = this.actions$.pipe(
    ofType<PrescriptionOnServerCreated>(PrescriptionActionTypes.PrescriptionOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.prescription.createPrescription(payload.prescription, payload.patientId).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPrescriptionError({ error: res.error });
          } else {
            this.store.dispatch(
              new FirePrescriptionSuccess({
                action_type: PrescriptionActionTypes.PrescriptionCreated,
              }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PrescriptionCreated({ prescription: res.data });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private prescription: PrescriptionService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
